import React from 'react'
import PropTypes from 'prop-types'
import { CONNECTION_TYPES, getConnectionIcon } from '../../constants/connections'

/**
 * ConnectionAvatar Component
 *
 * Displays a person's avatar with connection status badge
 *
 * Props:
 * - connection: Connection object with first_name, last_name, photo, connection_type
 * - size: Avatar size ('sm', 'md', 'lg', 'xl')
 * - showBadge: Whether to show connection badge
 * - onClick: Click handler
 */
function ConnectionAvatar({ connection, size = 'md', showBadge = true, onClick }) {
  const { first_name, last_name, photo, connection_type, is_online, is_minor } = connection

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
    xl: 'w-20 h-20 text-2xl'
  }

  const badgeSizeClasses = {
    sm: 'w-3 h-3 text-[8px]',
    md: 'w-4 h-4 text-xs',
    lg: 'w-5 h-5 text-sm',
    xl: 'w-6 h-6 text-base'
  }

  // Get initials
  const getInitials = () => {
    const firstInitial = first_name ? first_name.charAt(0).toUpperCase() : ''
    const lastInitial = last_name ? last_name.charAt(0).toUpperCase() : ''
    return `${firstInitial}${lastInitial}` || 'U'
  }

  // Get badge content based on connection type
  const getBadge = () => {
    if (!showBadge) return null

    // Online indicator for connected users
    if (connection_type === CONNECTION_TYPES.CONNECTED && is_online) {
      return (
        <div className={`absolute bottom-0 right-0 ${badgeSizeClasses[size]} bg-green-500 rounded-full border-2 border-white`}></div>
      )
    }

    // Minor badge
    if (connection_type === CONNECTION_TYPES.MINOR || is_minor) {
      return (
        <div className={`absolute bottom-0 right-0 ${badgeSizeClasses[size]} bg-blue-500 rounded-full border-2 border-white flex items-center justify-center`}>
          <span className="text-white">👶</span>
        </div>
      )
    }

    // Pending badge
    if (connection_type === CONNECTION_TYPES.PENDING) {
      return (
        <div className={`absolute bottom-0 right-0 ${badgeSizeClasses[size]} bg-yellow-500 rounded-full border-2 border-white flex items-center justify-center`}>
          <span className="text-white">⏳</span>
        </div>
      )
    }

    // Tree only badge
    if (connection_type === CONNECTION_TYPES.TREE_ONLY) {
      return (
        <div className={`absolute bottom-0 right-0 ${badgeSizeClasses[size]} bg-gray-500 rounded-full border-2 border-white flex items-center justify-center`}>
          <span className="text-white">🌳</span>
        </div>
      )
    }

    return null
  }

  return (
    <div
      className={`relative ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Avatar */}
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-[#556B2F] flex items-center justify-center font-bold text-white`}>
        {photo ? (
          <img
            src={photo}
            alt={`${first_name} ${last_name}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <span>{getInitials()}</span>
        )}
      </div>

      {/* Connection Badge */}
      {getBadge()}
    </div>
  )
}

ConnectionAvatar.propTypes = {
  connection: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string,
    photo: PropTypes.string,
    connection_type: PropTypes.string.isRequired,
    is_online: PropTypes.bool,
    is_minor: PropTypes.bool
  }).isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  showBadge: PropTypes.bool,
  onClick: PropTypes.func
}

export default ConnectionAvatar
